import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TravelEntryCard } from '../components/admin/TravelEntryCard';
import { EditTravelEntryDialog } from '../components/admin/EditTravelEntryDialog';
import { TRAVEL_LABELS as LABELS } from '../components/admin/travelLabels';
import { Plane, Loader2, Download, LogOut, Users, AlertCircle } from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { adminApi, escapeCsvValue, downloadCsv } from '../lib/adminApi';

export const AdminTravel = () => {
  const { verified, logout } = useAdminAuth();
  const [entries, setEntries] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editEntry, setEditEntry] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [entriesRes, statusRes] = await Promise.all([
        adminApi.get('/api/travel-form'),
        adminApi.get('/api/travel-form/status')
      ]);
      setEntries(entriesRes.data);
      setStatus(statusRes.data);
      setError(null);
    } catch {
      setError('Failed to load travel form data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (verified) fetchData();
  }, [verified, fetchData]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete travel form entry for ${name}?`)) return;
    try {
      await adminApi.delete(`/api/travel-form/${id}`);
      await fetchData();
    } catch (err) {
      alert(`Failed to delete: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleSaved = (updated) => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  const exportToCSV = () => {
    if (entries.length === 0) {
      alert('No entries to export');
      return;
    }
    const headers = ['Name', 'Is Child', 'Phone', 'Travel Mode', 'Arrival Airport', 'Arrival Date', 'Arrival Time', 'Arrival Flight',
      'Departure Airport', 'Departure Date', 'Departure Time', 'Departure Flight', 'Transfer to Hotel', 'Transfer to Airport',
      'Hotel', 'Cold Appetizer', 'Main Course', 'Kids Appetizer', 'Kids Main', 'Allergies', 'Drinks', 'Child Info', 'Beauty Service', 'Comments', 'Submitted At'];
    const rows = [headers.join(',')];
    entries.forEach(e => {
      rows.push([
        escapeCsvValue(e.guestName), e.isChild ? 'Yes' : 'No', escapeCsvValue(e.phone),
        e.isChild ? '' : (e.travelMode === 'other' ? 'Other (not flying)' : 'Flight'),
        escapeCsvValue(e.arrivalAirport === 'other' ? e.arrivalAirportOther : LABELS.airport[e.arrivalAirport] || e.arrivalAirport),
        escapeCsvValue(e.arrivalDate), escapeCsvValue(e.arrivalTime), escapeCsvValue(e.arrivalFlight),
        escapeCsvValue(e.departureAirport === 'other' ? e.departureAirportOther : LABELS.airport[e.departureAirport] || e.departureAirport),
        escapeCsvValue(e.departureDate), escapeCsvValue(e.departureTime), escapeCsvValue(e.departureFlight),
        escapeCsvValue(e.transferArrival === 'other' ? e.transferArrivalOther : LABELS.transfer[e.transferArrival] || e.transferArrival),
        escapeCsvValue(e.transferDeparture === 'other' ? e.transferDepartureOther : LABELS.transfer[e.transferDeparture] || e.transferDeparture),
        escapeCsvValue(e.hotel), escapeCsvValue(LABELS.cold[e.coldAppetizer] || e.coldAppetizer), escapeCsvValue(LABELS.main[e.mainCourse] || e.mainCourse),
        escapeCsvValue(LABELS.kidsAppetizer[e.kidsAppetizer] || e.kidsAppetizer || ''),
        escapeCsvValue(LABELS.kidsMain[e.kidsMain] || e.kidsMain || ''),
        escapeCsvValue((e.allergies || []).map(a => a === 'other' ? e.allergiesOther : LABELS.allergy[a] || a).join('; ')),
        escapeCsvValue((e.drinks || []).map(d => LABELS.drink[d] || d).join('; ')),
        escapeCsvValue(e.travelingWithChild), escapeCsvValue(LABELS.beauty[e.beautyService] || e.beautyService),
        escapeCsvValue(e.comments), escapeCsvValue(new Date(e.submittedAt).toLocaleString())
      ].join(','));
    });
    downloadCsv(rows, `travel-forms-${new Date().toISOString().split('T')[0]}.csv`);
  };

  if (!verified || loading) {
    return (
      <div className="min-h-screen cream-bg">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-12 h-12 blue-text animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen cream-bg">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-red-600 text-xl">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cream-bg">
      <Navbar />

      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Plane className="w-12 h-12 blue-text mx-auto mb-6" />
            <h1 className="font-serif text-5xl md:text-6xl font-semibold blue-text mb-4" data-testid="admin-travel-title">
              Travel Forms
            </h1>
            <p className="font-sans text-lg blue-text opacity-80">Guest Travel & Flight Details</p>
          </div>

          <div className="flex justify-center gap-4 flex-wrap">
            <Link to="/admin">
              <Button type="button" variant="outline" className="border-2 border-primary blue-text hover:bg-blue-50" data-testid="back-to-rsvps-btn">
                <Users className="w-4 h-4 mr-2" />
                RSVP Dashboard
              </Button>
            </Link>
            <Button
              type="button"
              onClick={exportToCSV}
              disabled={entries.length === 0}
              className="blue-bg text-white hover:opacity-90 flex items-center gap-2"
              data-testid="export-travel-csv-btn"
            >
              <Download className="w-4 h-4" />
              Export to CSV
            </Button>
            <Button type="button" onClick={logout} variant="outline" className="border-2 border-primary blue-text hover:bg-blue-50" data-testid="admin-travel-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </section>

      {status && (
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-2 border-primary cream-bg">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold blue-text mb-2" data-testid="stat-total-guests">{status.totalGuests}</div>
                  <div className="text-sm font-sans blue-text opacity-80">Total Attending Guests</div>
                </CardContent>
              </Card>
              <Card className="border-2 border-green-500 cream-bg">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2" data-testid="stat-submitted">{status.submittedCount}</div>
                  <div className="text-sm font-sans text-green-600">
                    Filled the Form
                    {entries.filter(e => e.isChild).length > 0 && (
                      <span className="block text-xs opacity-80" data-testid="stat-children-note">
                        + {entries.filter(e => e.isChild).length} child(ren)
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-2 border-amber-500 cream-bg">
                <CardContent className="pt-6 text-center">
                  <div className="text-4xl font-bold text-amber-600 mb-2" data-testid="stat-not-submitted">{status.notSubmittedCount}</div>
                  <div className="text-sm font-sans text-amber-600">Not Filled Yet</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {status && status.notSubmitted.length > 0 && (
        <section className="pb-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-2 border-amber-500 cream-bg" data-testid="not-submitted-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl blue-text flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Guests Who Have NOT Filled the Form ({status.notSubmittedCount})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {status.notSubmitted.map((name) => (
                    <Badge key={name} variant="outline" className="border-amber-500 text-amber-700 bg-amber-50 px-3 py-1 text-sm font-normal">
                      {name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl font-semibold blue-text mb-6">Submissions ({entries.length})</h2>
          {entries.length === 0 ? (
            <Card className="border-2 border-primary cream-bg">
              <CardContent className="pt-8 pb-8 text-center">
                <p className="blue-text opacity-70" data-testid="no-submissions-message">No travel forms submitted yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {entries.map((entry) => (
                <TravelEntryCard key={entry.id} entry={entry} onDelete={handleDelete} onEdit={setEditEntry} />
              ))}
            </div>
          )}
        </div>
      </section>

      <EditTravelEntryDialog
        entry={editEntry}
        open={!!editEntry}
        onOpenChange={(open) => !open && setEditEntry(null)}
        onSaved={handleSaved}
      />

      <Footer />
    </div>
  );
};
